/**
 * Bill Pay Page
 * 4-week upcoming payment schedule with mark-paid and grace period
 * support. Shows payments grouped by week with totals. Unpaid bills from
 * earlier weeks carry into the current week as overdue until marked paid.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CreditCard,
  Landmark,
  Repeat,
  Receipt,
  Loader2,
  CheckCircle2,
  Circle,
  CalendarClock,
  DollarSign,
  AlertTriangle,
  History,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ScheduledPayment {
  sourceId: string;
  source: string;
  name: string;
  lender: string | null;
  paymentType: string;
  dueDate: string;
  minimumPayment: number;
  paymentAmount: number;
  currentBalance: number;
  interestRate: number;
  monthlyInterest: number;
  autopayEnabled: boolean;
  graceDays: number;
  paymentStatus: string;
  paidDate: string | null;
  paidAmount: number | null;
  notes: string | null;
  scheduleId: string | null;
  weekIndex: number;
}

interface PaidPayment {
  scheduleId: string;
  source: string;
  sourceId: string;
  name: string;
  lender: string | null;
  paymentType: string;
  dueDate: string;
  paidDate: string;
  paidAmount: number | null;
  notes: string | null;
  wasLate: boolean;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const paymentTypeLabel: Record<string, string> = {
  credit_card: 'Credit Card',
  store_card: 'Store Card',
  business_line: 'Business Line',
  mortgage: 'Mortgage',
  auto: 'Auto Loan',
  equipment: 'Equipment Loan',
  business: 'Business Loan',
  personal: 'Personal Loan',
  line_of_credit: 'Line of Credit',
  subscription: 'Subscription',
  expense: 'Recurring Expense',
  other: 'Other',
};

function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day;
  const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
  return weekStart.toISOString().split('T')[0];
}

function formatWeekRange(startDate: string, weekIndex: number): { label: string; start: string; end: string } {
  const start = new Date(startDate);
  start.setDate(start.getDate() + weekIndex * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const label = weekIndex === 0 ? 'This Week' : weekIndex === 1 ? 'Next Week' : `Week ${weekIndex + 1}`;

  return {
    label,
    start: start.toLocaleDateString('en-US', opts),
    end: end.toLocaleDateString('en-US', opts),
  };
}

// Due/paid dates come from SQL DATE columns and serialize as UTC midnight, so
// all date-only math and formatting below stays in UTC. Parsing or rendering
// them in local time shifts them a day for any timezone behind UTC.
function toDateOnly(value: string): string {
  return value.split('T')[0];
}

function parseDateOnly(value: string): Date {
  return new Date(`${toDateOnly(value)}T00:00:00Z`);
}

function formatDateOnly(value: string): string {
  return parseDateOnly(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function daysBetween(fromDate: string, toDate: string): number {
  return Math.round((parseDateOnly(toDate).getTime() - parseDateOnly(fromDate).getTime()) / 86400000);
}

/** Last day a payment can be made before it counts as late. */
function graceEndDate(pmt: ScheduledPayment): string {
  const end = parseDateOnly(pmt.dueDate);
  end.setUTCDate(end.getUTCDate() + pmt.graceDays);
  return end.toISOString().split('T')[0];
}

/** History defaults to the last three months of recorded payments. */
function defaultHistoryFrom(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 3);
  return d.toISOString().split('T')[0];
}

/** Stable identity for a payment period: source + record + due date. */
function paymentKey(pmt: ScheduledPayment): string {
  return `${pmt.source}-${pmt.sourceId}-${toDateOnly(pmt.dueDate)}`;
}

/** Unpaid and past its grace period. */
function isOverduePayment(pmt: ScheduledPayment, today: string): boolean {
  return pmt.paymentStatus !== 'paid' && graceEndDate(pmt) < today;
}

export function BillPayPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [profileFilter, setProfileFilter] = useState('');
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [payingPayment, setPayingPayment] = useState<ScheduledPayment | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState('schedule');
  const [historyFrom, setHistoryFrom] = useState(defaultHistoryFrom());
  const [historyTo, setHistoryTo] = useState(new Date().toISOString().split('T')[0]);

  const startDate = getWeekStart();

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['paymentSchedule', { startDate, businessProfileId: profileFilter }],
    queryFn: () => api.getPaymentSchedule({
      startDate,
      weeks: 4,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['paymentHistory', { historyFrom, historyTo, businessProfileId: profileFilter }],
    queryFn: () => api.getPaymentHistory({
      from: historyFrom,
      to: historyTo,
      businessProfileId: profileFilter || undefined,
    }),
    // Only fetch once the history tab is actually opened
    enabled: tab === 'history',
  });

  const history = (historyData?.data || []) as PaidPayment[];
  const historyTotal = history.reduce((sum, h) => sum + (h.paidAmount || 0), 0);

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const payments = (data?.data || []) as ScheduledPayment[];

  // Group by week
  const weeks: Map<number, ScheduledPayment[]> = new Map();
  for (let i = 0; i < 4; i++) weeks.set(i, []);
  for (const pmt of payments) {
    const wi = Math.max(0, Math.min(3, pmt.weekIndex));
    weeks.get(wi)!.push(pmt);
  }

  const invalidateSchedule = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentSchedule'] });
    queryClient.invalidateQueries({ queryKey: ['upcomingPayments'] });
    queryClient.invalidateQueries({ queryKey: ['paymentHistory'] });
  };

  const markPaidMutation = useMutation({
    mutationFn: (data: { source: string; sourceId: string; dueDate: string; paidAmount?: number; paidDate?: string; notes?: string }) =>
      api.markPaymentPaid(data),
    onSuccess: () => {
      invalidateSchedule();
      setPaidDialogOpen(false);
      setPayingPayment(null);
      toast({ title: 'Payment saved' });
    },
  });

  const unmarkMutation = useMutation({
    mutationFn: (data: { source: string; sourceId: string; dueDate: string }) =>
      api.unmarkPayment(data),
    onSuccess: () => {
      invalidateSchedule();
      setPaidDialogOpen(false);
      setPayingPayment(null);
      toast({ title: 'Payment unmarked' });
    },
  });

  const batchPaidMutation = useMutation({
    mutationFn: (data: {
      payments: { source: string; sourceId: string; dueDate: string; paidAmount?: number; paidDate?: string }[];
    }) => api.markPaymentsPaidBatch(data),
    onSuccess: (_res, vars) => {
      invalidateSchedule();
      setSelected(new Set());
      toast({ title: `${vars.payments.length} payment${vars.payments.length !== 1 ? 's' : ''} marked paid` });
    },
  });

  function toggleSelect(pmt: ScheduledPayment) {
    const key = paymentKey(pmt);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  /** Select or clear every still-unpaid payment in a group. */
  function toggleSelectGroup(group: ScheduledPayment[], select: boolean) {
    const keys = group.filter(p => p.paymentStatus !== 'paid').map(paymentKey);
    setSelected(prev => {
      const next = new Set(prev);
      for (const k of keys) {
        if (select) next.add(k); else next.delete(k);
      }
      return next;
    });
  }

  /**
   * Mark every selected payment paid. `onDueDate` records each bill's own due
   * date as the payment date (the common "I paid these on time" catch-up);
   * otherwise today is used.
   */
  function handleBatchMarkPaid(onDueDate: boolean) {
    if (!selectedPayments.length) return;
    batchPaidMutation.mutate({
      payments: selectedPayments.map(p => ({
        source: p.source,
        sourceId: p.sourceId,
        dueDate: toDateOnly(p.dueDate),
        paidAmount: p.paymentAmount,
        paidDate: onDueDate ? toDateOnly(p.dueDate) : today,
      })),
    });
  }

  function handleQuickPaid(pmt: ScheduledPayment) {
    setPayingPayment(pmt);
    setPaidDialogOpen(true);
  }

  // Both paid and unpaid rows open the dialog. Unmarking lives inside it so a
  // stray click can't wipe a recorded amount and date, and so a wrong date can
  // be corrected without re-entering everything.
  function handleTogglePaid(pmt: ScheduledPayment) {
    handleQuickPaid(pmt);
  }

  function handleUnmark(pmt: ScheduledPayment) {
    unmarkMutation.mutate({
      source: pmt.source,
      sourceId: pmt.sourceId,
      dueDate: toDateOnly(pmt.dueDate),
    });
  }

  function handlePaidSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!payingPayment) return;
    const formData = new FormData(e.currentTarget);
    const amountStr = formData.get('paidAmount') as string;
    markPaidMutation.mutate({
      source: payingPayment.source,
      sourceId: payingPayment.sourceId,
      dueDate: toDateOnly(payingPayment.dueDate),
      paidAmount: amountStr ? parseFloat(amountStr) : undefined,
      paidDate: (formData.get('paidDate') as string) || undefined,
      notes: (formData.get('notes') as string) || undefined,
    });
  }

  const today = new Date().toISOString().split('T')[0];

  // Selection is keyed by payment period, so rows that get paid (or drop out of
  // the window) simply stop matching rather than lingering as stale keys.
  const selectedPayments = payments.filter(
    p => p.paymentStatus !== 'paid' && selected.has(paymentKey(p))
  );
  const selectedTotal = selectedPayments.reduce((s, p) => s + p.paymentAmount, 0);

  // Summary totals
  const totalDue = payments.filter(p => p.paymentStatus !== 'paid').reduce((s, p) => s + p.paymentAmount, 0);
  const totalPaid = payments.filter(p => p.paymentStatus === 'paid').reduce((s, p) => s + (p.paidAmount || p.paymentAmount), 0);
  const totalAll = payments.reduce((s, p) => s + p.paymentAmount, 0);
  const paidCount = payments.filter(p => p.paymentStatus === 'paid').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bill Pay</h1>
          <p className="text-muted-foreground">Track what's due and review what you've already paid</p>
        </div>
        {profiles.length > 1 && (
          <Select value={profileFilter} onValueChange={(v) => setProfileFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All profiles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All profiles</SelectItem>
              {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="schedule">Bill Pay</TabsTrigger>
          <TabsTrigger value="history">Payment History</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule" className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            4-week payment schedule — check off paid bills as you pay them; unpaid bills stay listed as overdue
          </p>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="h-5 w-5 text-primary" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">4-Week Total</p>
                    <p className="text-xl font-bold">{formatCurrency(totalAll)}</p>
                    <p className="text-xs text-muted-foreground">{payments.length} payment{payments.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10"><CalendarClock className="h-5 w-5 text-amber-500" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Remaining Due</p>
                    <p className="text-xl font-bold">{formatCurrency(totalDue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle2 className="h-5 w-5 text-green-500" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Paid</p>
                    <p className="text-xl font-bold">{formatCurrency(totalPaid)}</p>
                    <p className="text-xs text-muted-foreground">{paidCount} of {payments.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Overdue</p>
                    <p className="text-xl font-bold">
                      {payments.filter(p => isOverduePayment(p, today)).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bulk action bar — only present while something is selected */}
          {selectedPayments.length > 0 && (
            <Card className="border-primary">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="text-sm">
                  <span className="font-medium">
                    {selectedPayments.length} bill{selectedPayments.length !== 1 ? 's' : ''} selected
                  </span>
                  <span className="text-muted-foreground ml-2">{formatCurrency(selectedTotal)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleBatchMarkPaid(true)}
                    disabled={batchPaidMutation.isPending}
                  >
                    {batchPaidMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Mark paid on due date
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBatchMarkPaid(false)}
                    disabled={batchPaidMutation.isPending}
                  >
                    Mark paid today
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Weekly Grid */}
          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : payments.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No upcoming payments</p>
                <p className="text-sm text-muted-foreground mt-1">Set a payment due day on your loans or add charge accounts to see them here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-6 items-start">
              {Array.from(weeks.entries()).map(([weekIdx, weekPayments]) => {
                const weekInfo = formatWeekRange(startDate, weekIdx);
                const weekTotal = weekPayments.reduce((s, p) => s + p.paymentAmount, 0);
                const weekPaid = weekPayments.filter(p => p.paymentStatus === 'paid').length;
                const weekPending = weekPayments.length - weekPaid;

                return (
                  <div key={weekIdx} className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        {weekPending > 0 && (
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            aria-label={`Select all unpaid bills in ${weekInfo.label}`}
                            title={`Select all unpaid bills in ${weekInfo.label}`}
                            checked={weekPayments
                              .filter(p => p.paymentStatus !== 'paid')
                              .every(p => selected.has(paymentKey(p)))}
                            onChange={e => toggleSelectGroup(weekPayments, e.target.checked)}
                          />
                        )}
                        <h3 className="text-lg font-semibold">{weekInfo.label}</h3>
                        <span className="text-sm text-muted-foreground">{weekInfo.start} &ndash; {weekInfo.end}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {weekPaid > 0 && (
                          <Badge variant="success" className="text-xs">{weekPaid} paid</Badge>
                        )}
                        {weekPending > 0 && (
                          <Badge variant="warning" className="text-xs">{weekPending} pending</Badge>
                        )}
                        <span className="text-sm font-bold">{formatCurrency(weekTotal)}</span>
                      </div>
                    </div>

                    {weekPayments.length === 0 ? (
                      <Card>
                        <CardContent className="py-6 text-center text-sm text-muted-foreground">
                          No payments due this week
                        </CardContent>
                      </Card>
                    ) : (
                      <Card>
                        <CardContent className="p-0">
                          <div className="divide-y">
                            {weekPayments.map((pmt) => {
                              const isPaid = pmt.paymentStatus === 'paid';
                              const dueDate = toDateOnly(pmt.dueDate);
                              const isPastDue = !isPaid && dueDate < today;
                              const isOverdue = isOverduePayment(pmt, today);
                              // Past the due date but still inside the grace period
                              const withinGrace = isPastDue && !isOverdue;
                              const daysLate = isPastDue ? daysBetween(dueDate, today) : 0;
                              // Loans bill P&I (minimumPayment) plus escrow and extra
                              // principal. Show the split so the total is verifiable.
                              // Only loans compose their amount this way — on a card
                              // the gap is just paying above the minimum.
                              const addOnAmount = pmt.source === 'loan'
                                ? pmt.paymentAmount - pmt.minimumPayment
                                : 0;

                              return (
                                <div
                                  key={`${pmt.source}-${pmt.sourceId}-${pmt.dueDate}`}
                                  className={`flex items-center gap-3 p-3 ${isPaid ? 'bg-green-500/5 opacity-75' : isOverdue ? 'bg-red-500/5' : 'hover:bg-muted/30'}`}
                                >
                                  {/* Bulk-select box — unpaid rows only, so it never
                                      sits next to an already-paid tick */}
                                  <div className="flex-shrink-0 w-4">
                                    {!isPaid && (
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        aria-label={`Select ${pmt.name}`}
                                        checked={selected.has(paymentKey(pmt))}
                                        onChange={() => toggleSelect(pmt)}
                                      />
                                    )}
                                  </div>

                                  {/* Check button */}
                                  <button
                                    onClick={() => handleTogglePaid(pmt)}
                                    title={isPaid ? 'Edit or undo this payment' : 'Mark this bill paid'}
                                    className="flex-shrink-0"
                                    disabled={markPaidMutation.isPending || unmarkMutation.isPending}
                                  >
                                    {isPaid ? (
                                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                                    ) : (
                                      <Circle className="h-6 w-6 text-muted-foreground hover:text-primary" />
                                    )}
                                  </button>

                                  {/* Icon */}
                                  <div className="flex-shrink-0">
                                    {pmt.source === 'charge_account' ? <CreditCard className="h-5 w-5 text-muted-foreground" />
                                      : pmt.source === 'subscription' ? <Repeat className="h-5 w-5 text-muted-foreground" />
                                      : pmt.source === 'expense' ? <Receipt className="h-5 w-5 text-muted-foreground" />
                                      : <Landmark className="h-5 w-5 text-muted-foreground" />}
                                  </div>

                                  {/* Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className={`font-medium ${isPaid ? 'line-through text-muted-foreground' : ''}`}>
                                        {pmt.name}
                                      </span>
                                      {pmt.lender && (
                                        <span className="text-xs text-muted-foreground">({pmt.lender})</span>
                                      )}
                                      {pmt.autopayEnabled && (
                                        <Badge variant="secondary" className="text-xs">Autopay</Badge>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mt-0.5">
                                      <span>{paymentTypeLabel[pmt.paymentType] || pmt.paymentType}</span>
                                      <span>&middot;</span>
                                      <span>Due {formatDateOnly(pmt.dueDate)}</span>
                                      {addOnAmount > 0 && (
                                        <span title="Principal & interest plus escrow and any extra principal">
                                          &middot; {formatCurrency(pmt.minimumPayment)} P&amp;I + {formatCurrency(addOnAmount)} escrow/extra
                                        </span>
                                      )}
                                      {pmt.graceDays > 0 && (
                                        <span>&middot; {pmt.graceDays}d grace</span>
                                      )}
                                      {withinGrace && (
                                        <Badge variant="info" className="text-xs">In grace</Badge>
                                      )}
                                      {isOverdue && (
                                        <Badge variant="destructive" className="text-xs">
                                          Overdue {daysLate}d
                                        </Badge>
                                      )}
                                      {isPaid && pmt.paidDate && (
                                        <span className="text-green-600">
                                          Paid {formatDateOnly(pmt.paidDate)}
                                          {pmt.paidAmount != null && ` — ${formatCurrency(pmt.paidAmount)}`}
                                        </span>
                                      )}
                                    </div>
                                    {pmt.notes && (
                                      <p className="text-xs text-muted-foreground mt-1 italic">{pmt.notes}</p>
                                    )}
                                  </div>

                                  {/* Interest */}
                                  {pmt.monthlyInterest > 0 && (
                                    <div className="text-right flex-shrink-0">
                                      <span className="text-xs text-amber-600">{formatCurrency(pmt.monthlyInterest)} int.</span>
                                    </div>
                                  )}

                                  {/* Amount */}
                                  <div className="text-right flex-shrink-0 w-24">
                                    <span className={`text-lg font-bold ${isPaid ? 'text-green-600 line-through' : ''}`}>
                                      {formatCurrency(pmt.paymentAmount)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Every payment you've recorded, by the date it was paid
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="historyFrom" className="text-xs">From</Label>
                <Input
                  type="date"
                  id="historyFrom"
                  className="h-9"
                  value={historyFrom}
                  max={historyTo}
                  onChange={e => setHistoryFrom(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="historyTo" className="text-xs">To</Label>
                <Input
                  type="date"
                  id="historyTo"
                  className="h-9"
                  value={historyTo}
                  min={historyFrom}
                  onChange={e => setHistoryTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {historyLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : history.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <History className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No payments recorded in this range</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Nothing between {formatDateOnly(historyFrom)} and {formatDateOnly(historyTo)} — try widening the dates
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle2 className="h-5 w-5 text-green-500" /></div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Paid</p>
                        <p className="text-xl font-bold">{formatCurrency(historyTotal)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10"><Receipt className="h-5 w-5 text-primary" /></div>
                      <div>
                        <p className="text-sm text-muted-foreground">Payments</p>
                        <p className="text-xl font-bold">{history.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-500/10"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
                      <div>
                        <p className="text-sm text-muted-foreground">Paid Late</p>
                        <p className="text-xl font-bold">{history.filter(h => h.wasLate).length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="p-3 font-medium">Paid</th>
                          <th className="p-3 font-medium">Bill</th>
                          <th className="p-3 font-medium">Type</th>
                          <th className="p-3 font-medium">Due</th>
                          <th className="p-3 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {history.map(h => (
                          <tr key={h.scheduleId} className="hover:bg-muted/30">
                            <td className="p-3 whitespace-nowrap">{formatDateOnly(h.paidDate)}</td>
                            <td className="p-3">
                              <span className="font-medium">{h.name}</span>
                              {h.lender && (
                                <span className="text-xs text-muted-foreground ml-2">({h.lender})</span>
                              )}
                              {h.notes && (
                                <p className="text-xs text-muted-foreground italic">{h.notes}</p>
                              )}
                            </td>
                            <td className="p-3 text-muted-foreground whitespace-nowrap">
                              {paymentTypeLabel[h.paymentType] || h.paymentType}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="text-muted-foreground">{formatDateOnly(h.dueDate)}</span>
                              {h.wasLate && (
                                <Badge variant="destructive" className="text-xs ml-2">Late</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right font-medium whitespace-nowrap">
                              {h.paidAmount != null ? formatCurrency(h.paidAmount) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Mark Paid Dialog */}
      <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {payingPayment?.paymentStatus === 'paid' ? 'Edit Payment' : 'Mark Payment Paid'}
            </DialogTitle>
          </DialogHeader>
          {payingPayment && (
            <form onSubmit={handlePaidSubmit} className="space-y-4">
              <div className="text-sm">
                <span className="font-medium">{payingPayment.name}</span>
                <span className="text-muted-foreground ml-2">
                  Due {formatDateOnly(payingPayment.dueDate)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="paidAmount">Amount Paid</Label>
                  <Input
                    type="number"
                    name="paidAmount"
                    id="paidAmount"
                    step="0.01"
                    min="0"
                    defaultValue={payingPayment.paidAmount ?? payingPayment.paymentAmount}
                    placeholder={`Scheduled: ${formatCurrency(payingPayment.paymentAmount)}`}
                  />
                </div>
                <div>
                  <Label htmlFor="paidDate">Date Paid</Label>
                  <Input
                    type="date"
                    name="paidDate"
                    id="paidDate"
                    max={today}
                    defaultValue={payingPayment.paidDate ? toDateOnly(payingPayment.paidDate) : today}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="paidNotes">Notes</Label>
                <Textarea
                  name="notes"
                  id="paidNotes"
                  rows={2}
                  placeholder="Optional"
                  defaultValue={payingPayment.notes || ''}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                {payingPayment.paymentStatus === 'paid' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => handleUnmark(payingPayment)}
                    disabled={unmarkMutation.isPending}
                  >
                    {unmarkMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Unmark as Paid
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setPaidDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={markPaidMutation.isPending}>
                    {markPaidMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {payingPayment.paymentStatus === 'paid' ? 'Save Changes' : 'Mark Paid'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
